import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore, type ShowFortuneParams } from '@/hooks/use-app-store';
import { FortuneRatings } from './FortuneRatings';
import { generatePersonalizedFortuneFrame } from '@/utils/fortune-frame-generator';
import { CameraIcon, XIcon, Star } from 'lucide-react';
import { useFortuneDisplay } from '@/hooks/use-fortune-display';

interface FortuneDisplayProps {
  data: ShowFortuneParams;
  onClose: () => void;
}

const FortuneAnimation: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-900/90 via-blue-900/90 to-indigo-900/90 flex items-center justify-center z-50 overflow-hidden">
      <div className="text-center relative">
        {/* Random AI generated stars... */}
        <div className="relative w-80 h-80 mx-auto">
          {/* Main mega star */}
          <Star
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 text-yellow-400 drop-shadow-2xl"
            style={{
              animation: 'spin 1s linear infinite, starPulse 2.3s ease-in-out forwards alternate, starGlow 2s ease-in-out infinite',
              filter: 'drop-shadow(0 0 20px #fbbf24) drop-shadow(0 0 40px #f59e0b)'
            }}
          />

          {/* Orbiting stars */}
          {/* {[...Array(8)].map((_, i) => (
            <Star
              key={i}
              className={`absolute w-12 h-12 text-yellow-300`}
              style={{
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-120px) rotate(-${i * 45}deg)`,
                animation: `orbit 3s linear infinite, spin 2s linear infinite reverse, starPulse ${1 + i * 0.2}s ease-in-out infinite alternate`,
                filter: 'drop-shadow(0 0 10px #fbbf24)',
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))} */}

          {/* Medium orbiting ring */}
          {/* {[...Array(12)].map((_, i) => (
            <Star
              key={`mid-${i}`}
              className={`absolute w-8 h-8 text-yellow-200`}
              style={{
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-80px) rotate(-${i * 30}deg)`,
                animation: `orbit 2s linear infinite reverse, spin 1.5s linear infinite, starPulse ${0.8 + i * 0.1}s ease-in-out infinite alternate`,
                filter: 'drop-shadow(0 0 8px #fbbf24)',
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))} */}

          {/* Inner fast ring */}
          {/* {[...Array(6)].map((_, i) => (
            <Star
              key={`inner-${i}`}
              className={`absolute w-6 h-6 text-yellow-100`}
              style={{
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateY(-50px) rotate(-${i * 60}deg)`,
                animation: `orbit 1s linear infinite, spin 3s linear infinite reverse, starPulse ${0.5 + i * 0.1}s ease-in-out infinite alternate`,
                filter: 'drop-shadow(0 0 6px #fbbf24)',
                animationDelay: `${i * 0.05}s`
              }}
            />
          ))} */}

          {/* Chaotic background stars */}
          {/* {[...Array(20)].map((_, i) => (
            <Star
              key={`chaos-${i}`}
              className={`absolute w-4 h-4 text-yellow-50 opacity-70`}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `spin ${0.5 + Math.random() * 2}s linear infinite, starPulse ${0.3 + Math.random() * 1}s ease-in-out infinite alternate, chaosFloat 4s ease-in-out infinite`,
                filter: 'drop-shadow(0 0 4px #fbbf24)',
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))} */}
        </div>

        {/* Mystical text with dramatic effects */}
        <div className="mt-8 space-y-4">
          <p className="text-white text-2xl font-bold tracking-wider"
             style={{
               animation: 'textGlow 1.5s ease-in-out infinite alternate, bounce 2s infinite',
               textShadow: '0 0 10px #ffffff, 0 0 20px #fbbf24, 0 0 30px #f59e0b'
             }}>
            神々の導きを受け取っています...
          </p>
          <p className="text-yellow-200 text-lg font-semibold tracking-wide"
             style={{
               animation: 'textGlow 1.8s ease-in-out infinite alternate',
               textShadow: '0 0 8px #fbbf24'
             }}>
            Receiving divine guidance...
          </p>
        </div>
      </div>

      {/* Add custom CSS animations */}
      <style>{`
        @keyframes orbit {
          from { transform: translate(-50%, -50%) rotate(0deg) translateY(var(--orbit-radius, -120px)) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg) translateY(var(--orbit-radius, -120px)) rotate(-360deg); }
        }

        @keyframes starPulse {
          0% { transform: scale(1) rotate(0deg); opacity: 0.8; }
          100% { transform: scale(1.3) rotate(1450deg); opacity: 1; }
        }

        @keyframes starGlow {
          0% { filter: drop-shadow(0 0 20px #fbbf24) drop-shadow(0 0 40px #f59e0b); }
          50% { filter: drop-shadow(0 0 30px #fbbf24) drop-shadow(0 0 60px #f59e0b) drop-shadow(0 0 80px #dc2626); }
          100% { filter: drop-shadow(0 0 20px #fbbf24) drop-shadow(0 0 40px #f59e0b); }
        }

        @keyframes textGlow {
          0% { text-shadow: 0 0 10px #ffffff, 0 0 20px #fbbf24, 0 0 30px #f59e0b; }
          100% { text-shadow: 0 0 15px #ffffff, 0 0 30px #fbbf24, 0 0 45px #f59e0b, 0 0 60px #dc2626; }
        }

        @keyframes chaosFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(90deg); }
          50% { transform: translateY(10px) rotate(180deg); }
          75% { transform: translateY(-10px) rotate(270deg); }
        }
      `}</style>
    </div>
  );
};

export const FortuneDisplay: React.FC<FortuneDisplayProps> = ({ data, onClose }) => {
  const navigate = useNavigate();
  const { setPhotoboothNavigationData } = useAppStore();
  const { fortuneText, fortuneLevel, locale = 'EN', ratings } = data;
  const [isGeneratingFrame, setIsGeneratingFrame] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(true);

  // Handle paper slap animation
  useEffect(() => {
    // Start animation immediately
    const timer = setTimeout(() => {
      setIsAnimatingIn(false);
    }, 2000); // Animation duration

    return () => clearTimeout(timer);
  }, []);

  // Map fortune level to luck index (1-7)
  const getLuckIndex = (level: string): number => {
    const luckMapping: { [key: string]: number } = {
      'Great Luck': 1, '大吉': 1,
      'Luck': 2, '吉': 2,
      'Fair Luck': 3, '中吉': 3,
      'A Little Luck': 4, '小吉': 4,
      'Half Luck': 5, '半吉': 5,
      'Uncertain Luck': 6, '末吉': 6,
      'Bad Luck': 7, '凶': 7
    };
    return luckMapping[level] || 1;
  };

  const luckIndex = getLuckIndex(fortuneLevel);
  const luckIndexFormatted = luckIndex.toString().padStart(2, '0');

  // Get image paths based on luck level and locale
  const backgroundImagePath = `/img/omikuji/Omikuji_UI_Fortune_BG_${luckIndexFormatted}.png`;
  const headerImagePath = `/img/omikuji/Omikuji_UI_${locale}_LUCK_${luckIndexFormatted}.png`;

  const handleTakePhoto = async () => {
    try {
      setIsGeneratingFrame(true);
      
      // Generate personalized frame
      const personalizedFrameUrl = await generatePersonalizedFortuneFrame({
        zodiacCard: {
          name: 'Celestial',
          image: 'default'
        },
        astrologyCard: {
          name: 'Mystic',
          image: 'default'
        },
        fortuneLevel: luckIndex,
        ratings
      });
      
      // Set navigation data for personalized photobooth
      setPhotoboothNavigationData({
        sourceExperience: 'fortune-telling',
        overlayImage: personalizedFrameUrl,
        theme: 'fortune',
        data: {
          fortuneLevel,
          luckIndex,
          ratings
        }
      });
      
      // Navigate to photobooth
      navigate('/photobooth/fortune-telling');
    } catch (error) {
      console.error('Failed to generate personalized frame:', error);
      
      // Fallback to default fortune overlay
      setPhotoboothNavigationData({
        sourceExperience: 'fortune-telling',
        theme: 'fortune',
        data: {
          fortuneLevel,
          luckIndex,
          ratings
        }
      });
      
      navigate('/photobooth/fortune-telling');
    } finally {
      setIsGeneratingFrame(false);
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col items-end justify-center z-10 p-4">
      <div
        className={`relative max-w-[50dvw] w-full max-h-[95dvh] mt-[12%] ${
          isAnimatingIn ? 'paper-appear-animation' : ''
        }`}
        style={{
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Fortune Background */}
        <div
          className="relative px-[28%] pt-[60%] pb-[15%]"
          style={{
            backgroundImage: `url(${backgroundImagePath})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            aspectRatio: '1040 / 2780', // Adjust based on actual image dimensions
            minHeight: '600px'
          }}
        >
          <div className="inset-0 flex flex-col">
            <div className="flex justify-center mb-6">
              <img
                src={headerImagePath}
                alt={`${fortuneLevel} header`}
              />
            </div>

            <div className="flex-1">
              <div className="mb-3">
                <FortuneRatings ratings={ratings} locale={locale} />
              </div>

              <div className="mb-6">
                <p
                  className="text-gray-600 leading-relaxed text-justified px-2"
                  style={{
                    fontSize: locale == 'JA' ? 'clamp(1rem, 3.5vw, 1.5rem)' : 'clamp(1rem, 3.5vw, 1.2rem)',
                    lineHeight: '1.6',
                    fontFamily: locale == 'JA' ? "'Yuji Syuku', serif" : 'Caprasimo, serif'
                  }}
                >
                  {fortuneText}
                </p>
              </div>
            </div>

            {/* Action Buttons - positioned at bottom of background */}
            <div className="absolute -bottom-12 left-0 right-0 flex gap-3 justify-center px-4">
              <button
                onClick={onClose}
                className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center shadow-xl"
              >
                <XIcon />
              </button>
              {/* <button
                onClick={handleTakePhoto}
                disabled={isGeneratingFrame}
                className={`w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center shadow-xl ${
                  isGeneratingFrame
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-600 to-rose-600'
                }`}
              >
                {isGeneratingFrame ? (
                  <>
                    <div className="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating Frame...
                  </>
                ) : (
                  <CameraIcon />
                )}
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Paper slapping animation styles */}
      <style>{`
        .paper-appear-animation {
          animation: paperZoom 1.8s cubic-bezier(.31,-0.19,.47,.97) forwards;
        }

        @keyframes paperZoom {
          0% {
            transform: scale(2) translateZ(-500px);
            opacity: 0;
            filter: blur(20px);
          }
          100% {
            transform: scale(1) translateZ(0px);
            opacity: 1;
            filter: blur(0px);
          }
        }

      `}</style>
    </div>
  );
};

export const FortuneDisplayContainer: React.FC = () => {
  const { fortuneDisplay, hideFortune } = useAppStore();
  const { isAnimating } = useFortuneDisplay();

  // Show animation when fortune is being generated
  if (isAnimating) {
    return <FortuneAnimation />;
  }

  // Show fortune display when ready
  if (!fortuneDisplay.isVisible || !fortuneDisplay.fortuneData) {
    return null;
  }

  return (
    <FortuneDisplay
      data={fortuneDisplay.fortuneData}
      onClose={hideFortune}
    />
  );
};